import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt';
import { Autocomplete, Button, CircularProgress, Stack, TextField } from '@mui/material';
import AdminUser from 'AdminUser';
import React from 'react';
import { adminLinkLabel } from './guardrailEditorUtils';

type GuardrailEditorToolbarProps = {
  onBold: () => void;
  onItalic: () => void;
  onLink: () => void;
  adminOptions: AdminUser[];
  adminLoading: boolean;
  onAdminSearch: (term: string) => void;
  onAdminSelect: (admin: AdminUser | null) => void;
};

// Keep the textarea focused (and its selection) when a toolbar button is pressed.
const keepFocus = (e: React.MouseEvent) => e.preventDefault();

function AdminLinkPicker({
  adminOptions,
  adminLoading,
  onAdminSearch,
  onAdminSelect,
}: Pick<
  GuardrailEditorToolbarProps,
  'adminOptions' | 'adminLoading' | 'onAdminSearch' | 'onAdminSelect'
>) {
  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 260 }}
      options={adminOptions}
      loading={adminLoading}
      filterOptions={(opts) => opts}
      value={null}
      blurOnSelect
      clearOnBlur
      getOptionLabel={(admin) => `${adminLinkLabel(admin)} (${admin.email})`}
      isOptionEqualToValue={(a, b) => a.externalId === b.externalId}
      onInputChange={(_, term) => onAdminSearch(term)}
      onChange={(_, admin) => onAdminSelect(admin)}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Insert admin as email link…"
          InputProps={{
            ...params.InputProps,
            startAdornment: <PersonAddIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5 }} />,
            endAdornment: (
              <>
                {adminLoading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

/** Formatting toolbar for the guardrail-message markdown editor. */
export function GuardrailEditorToolbar({
  onBold,
  onItalic,
  onLink,
  adminOptions,
  adminLoading,
  onAdminSearch,
  onAdminSelect,
}: GuardrailEditorToolbarProps) {
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        onMouseDown={keepFocus}
        onClick={onBold}
        startIcon={<FormatBoldIcon />}
      >
        Bold
      </Button>
      <Button
        size="small"
        variant="outlined"
        onMouseDown={keepFocus}
        onClick={onItalic}
        startIcon={<FormatItalicIcon />}
      >
        Italic
      </Button>
      <Button
        size="small"
        variant="outlined"
        onMouseDown={keepFocus}
        onClick={onLink}
        startIcon={<InsertLinkIcon />}
      >
        Link
      </Button>
      <AdminLinkPicker
        adminOptions={adminOptions}
        adminLoading={adminLoading}
        onAdminSearch={onAdminSearch}
        onAdminSelect={onAdminSelect}
      />
    </Stack>
  );
}
